import React, { FC, ReactText, useEffect, useState, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import FormGroup from 'components/form/FormGroup';
import Form from 'components/form/Form';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import API from 'core/Api';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useWizardContext } from 'store/wizard';
import { formatDate } from 'utils/helpers/formatters';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Accordion from 'react-bootstrap/Accordion';
import Card from 'react-bootstrap/Card';
import { useTranslation } from 'react-i18next';
import { ISelectOptionProps, IFormControlProps } from 'components/form/types';
import { useQueryClient } from 'react-query';
import { BiChevronLeft, BiChevronDown } from 'react-icons/bi';
import { FaInfoCircle } from 'react-icons/fa';
import ToastMessage from './ToastMessage';
import Wizard from '../../components/Wizard';
import WizardNavAndSubmit from '../../components/WizardNavAndSubmit';
import Files from './Files';
import buildWizardProps from '../../helpers';
import { IInputs } from './types';

interface IToken {
  token_id: number;
  name: string;
  type: string;
  is_required: boolean;
  is_disabled: boolean;
  options?: ISelectOptionProps[];
  hint?: string;
  date_format?: string;
  value?: string | number;
}

interface IUser {
  id: number;
  name: string;
}

interface IEmployeeToken {
  user: IUser;
  tokens: IToken[];
}

interface IEquivalentTokenDetails {
  token: IToken;
  userId?: number;
}

interface IProps {
  data?: any;
}

const StepThree: FC<IProps> = ({ data }: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [openCards, setOpenCards] = useState<Record<string, boolean>>({});
  const [showToast, setShowToast] = useState(false);
  const [userNameWithError, setUserNameWithError] = useState<string | null>(null);
  const errorNamesRef = useRef<string[]>([]);
  const { currentStep, setCurrentStep, totalSteps, setTotalSteps } = useWizardContext();
  const queryClient = useQueryClient();
  const { tokens: tokensObj, files, total_steps: totalStepsData, is_multilingual: isMultilingual } = data;

  const textParts = t('docWizard.step3.multiUserWarning').split('</br>');

  // Set total steps based on data because of the edit mode
  setTotalSteps(totalStepsData);
  const params = useParams<{ id: string | undefined }>();

  // Flag to recognize doc mode - edit or create
  const holderId = params.id || '';

  // Render tokens according to selected language
  let globalTokensBg: IToken[] = [];
  let globalTokensEn: IToken[] = [];

  if (tokensObj && tokensObj.bg && tokensObj.bg.global) {
    globalTokensBg = tokensObj.bg.global;
  } else if (tokensObj && tokensObj.bg) {
    globalTokensBg = tokensObj.bg;
  }

  if (tokensObj && tokensObj.en && tokensObj.en.global) {
    globalTokensEn = tokensObj.en.global;
  } else if (tokensObj && tokensObj.en) {
    globalTokensEn = tokensObj.en;
  }

  const globalTokens = isMultilingual ? [...globalTokensBg, ...globalTokensEn] : globalTokensBg;

  let employeeTokensBg: IEmployeeToken[] = [];
  let employeeTokensEn: IEmployeeToken[] = [];

  if (tokensObj && tokensObj.bg && tokensObj.bg.employee) {
    employeeTokensBg = tokensObj.bg.employee;
  }
  if (tokensObj && tokensObj.en && tokensObj.en.employee) {
    employeeTokensEn = tokensObj.en.employee;
  }

  const employeeTokens = isMultilingual ? [...employeeTokensBg, ...employeeTokensEn] : employeeTokensBg;

  const hasShownToastRef = useRef(false);

  // Set errors to inputs
  const onError = (errors: any) => {
    const errorNames = Object.keys(errors);

    errorNamesRef.current = errorNames;

    if (errorNames.length > 0 && !hasShownToastRef.current) {
      // Extract user ID from the first error (you can modify this to handle multiple errors if needed)
      const userIdWithError = errorNames[0].split('-').pop();

      // Find the user with this ID in employeeTokens
      const userWithError = employeeTokens.find(
        (empToken: IEmployeeToken) => empToken.user.id === Number(userIdWithError)
      );

      // Store the user's name
      if (userWithError) {
        setUserNameWithError(userWithError.user.name);
      }

      setShowToast(true);
      hasShownToastRef.current = true; // Mark the toast as shown
    }
  };

  // Build default form values
  const buildDefaultValues = () => {
    const defaultValues: any = {};

    const getDefaultDropdownOption = (token: IToken) => {
      if (!token.options?.length) {
        return null;
      }

      return (
        token.options.find((opt: ISelectOptionProps) => opt.selected) ||
        token.options.find((opt: ISelectOptionProps) => opt.value === token.value) ||
        null
      );
    };

    if (employeeTokens.length > 0) {
      employeeTokens.forEach((employeeToken: IEmployeeToken) => {
        employeeToken.tokens.forEach((token: IToken) => {
          if (token.type === 'dropdown') {
            defaultValues[`field-${token.type}-${token.token_id}-${employeeToken.user.id}`] =
              getDefaultDropdownOption(token);
            return;
          }

          if (token.type === 'datepicker') {
            if (token.value) {
              // Split by both characters: " " && "."
              const slicedDate = String(token.value).split(/[\s.]+/);
              let day = null;
              let month = null;
              let year = null;
              let hour = null;

              /**
               * Assign values here depending on date format
               *
               * yyyy - year is stored in slicedDate[0]
               * dd.MM. - day in slicedDate[0], month in slicedDate[1]
               * etc....
               */
              if (token.date_format === 'dd.MM.yyyy H:mm') [day, month, year, hour] = slicedDate;

              if (token.date_format === 'dd.MM.yyyy') [day, month, year] = slicedDate;

              if (token.date_format === 'dd.MM') [day, month] = slicedDate;

              if (token.date_format === 'yyyy') [year] = slicedDate;

              let date = '';
              if (hour) {
                date = `${year}-${month}-${day}T${hour}:00`;
              } else {
                date = [year, month, day]
                  .map((d, index) => {
                    if (index === 0 && !d) {
                      return new Date().getFullYear();
                    }
                    return d;
                  })
                  .filter(d => d)
                  .join('-');
              }

              defaultValues[`field-${token.type}-${token.token_id}-${employeeToken.user.id}`] = new Date(date);
            }
          } else {
            defaultValues[`field-${token.type}-${token.token_id}-${employeeToken.user.id}`] = token.value
              ? token.value.toString()
              : token.value;
          }
        });
      });
    }

    if (globalTokens.length > 0) {
      globalTokens.forEach((globalToken: IToken) => {
        if (globalToken.type === 'dropdown') {
          defaultValues[`field-${globalToken.type}-${globalToken.token_id}`] = getDefaultDropdownOption(globalToken);
        }

        if (globalToken.type === 'datepicker') {
          if (globalToken.value) {
            // Split by both characters: " " && "."
            const slicedDate = String(globalToken.value).split(/[\s.]+/);
            let day = null;
            let month = null;
            let year = null;
            let hour = null;

            /**
             * Assign values here depending on date format
             *
             * yyyy - year is stored in slicedDate[0]
             * dd.MM. - day in slicedDate[0], month in slicedDate[1]
             * etc....
             */
            if (globalToken.date_format === 'dd.MM.yyyy H:mm') [day, month, year, hour] = slicedDate;

            if (globalToken.date_format === 'dd.MM.yyyy') [day, month, year] = slicedDate;

            if (globalToken.date_format === 'dd.MM') [day, month] = slicedDate;

            if (globalToken.date_format === 'yyyy') [year] = slicedDate;

            let date = '';
            if (hour) {
              date = `${year}-${month}-${day}T${hour}:00`;
            } else {
              date = [year, month, day]
                .map((d, index) => {
                  if (index === 0 && !d) {
                    return new Date().getFullYear();
                  }
                  return d;
                })
                .filter(d => d)
                .join('-');
            }

            defaultValues[`field-${globalToken.type}-${globalToken.token_id}`] = new Date(date);
          }
        }

        if (globalToken.type === 'text' || globalToken.type === 'textarea' || globalToken.type === 'numeric-integer') {
          defaultValues[`field-${globalToken.type}-${globalToken.token_id}`] =
            globalToken.value === 0 ? globalToken.value.toString() : globalToken.value;
        }
      });
    }
    return defaultValues;
  };

  const methods = useForm<(fieldName: ReactText, error: { type: string; message: string }) => void>({
    defaultValues: buildDefaultValues(),
  });
  const { formState, reset, setError, getValues, setValue } = methods;
  const { isSubmitting } = formState;

  const [errorAccordion, setErrorAccordion] = useState(false);

  useEffect(() => {
    if (errorNamesRef.current.length > 0 && !errorAccordion) {
      const newOpenCards = { ...openCards };
      errorNamesRef.current.forEach(errorName => {
        const id = errorName.split('-')[3];
        if (id) {
          newOpenCards[id] = true;
        }
      });
      setOpenCards(newOpenCards);
      setErrorAccordion(true);
    }
  }, [errorNamesRef.current]);

  useEffect(() => {
    let initialValues;

    if (employeeTokens && employeeTokens.length > 0) {
      const storedValuesString = localStorage.getItem('formValues');
      if (holderId) {
        initialValues = null;
      } else if (storedValuesString) {
        const storedValues = JSON.parse(storedValuesString);
        Object.keys(storedValues).forEach(key => {
          if (
            key.includes('datepicker') &&
            typeof storedValues[key] === 'string' &&
            !Number.isNaN(Date.parse(storedValues[key]))
          ) {
            storedValues[key] = new Date(storedValues[key]);
          }
        });
        initialValues = storedValues;
      } else {
        initialValues = buildDefaultValues();
      }
    } else {
      initialValues = buildDefaultValues();
    }

    reset(initialValues);
  }, [data]);

  const onSubmit = (submitData: IInputs) => {
    const tokens: any[] = [];
    const users: any[] = [];

    const valuesToStore: Record<string, any> = { ...getValues() };
    Object.keys(valuesToStore).forEach(key => {
      if (valuesToStore[key] instanceof Date) {
        valuesToStore[key] = valuesToStore[key].toString();
      }
    });
    localStorage.setItem('formValues', JSON.stringify(valuesToStore));

    Object.keys(submitData).forEach(key => {
      const parts = key.split('-');
      parts.shift();

      let userId: string | undefined;
      let tokenId: string | undefined;

      const lastPart = parts[parts.length - 1];
      const secondLastPart = parts[parts.length - 2];

      if (!Number.isNaN(Number(lastPart)) && !Number.isNaN(Number(secondLastPart))) {
        userId = parts.pop();
        tokenId = parts.pop();
      } else if (!Number.isNaN(Number(lastPart))) {
        tokenId = parts.pop();
      }

      const tokenType = parts.join('-');

      let value = submitData[key];

      // Returns if value is empty or array (files are arrays)
      if (!value || Array.isArray(value)) {
        return;
      }

      if (value && tokenType === 'dropdown') value = value.value;

      if (value && tokenType === 'datepicker') {
        const matchingUsers = employeeTokens.filter((emp: IEmployeeToken) => emp.user.id === Number(userId));
        let token: IToken | undefined;

        // Loop through matching users and find the token
        const userWithToken = matchingUsers.find((user: any) =>
          user.tokens.some((tkn: IToken) => tkn.token_id === Number(tokenId))
        );

        if (userWithToken) {
          token = userWithToken.tokens.find((tkn: IToken) => tkn.token_id === Number(tokenId));
        }

        if (!token) {
          token = globalTokens.find((tkn: IToken) => tkn.token_id === Number(tokenId));
        }
        let format = 'YYY';
        if (token) {
          if (token.date_format === 'dd.MM.yyyy H:mm') format = 'dd.mm.YYY hh:ii';
          if (token.date_format === 'dd.MM.yyyy') format = 'dd.mm.YYY';
          if (token.date_format === 'dd.MM') format = 'dd.mm';
        }

        value = formatDate(value, format);
      }

      // Convert \n new lines to <br/> tags
      if (typeof value === 'string' && tokenType === 'textarea') {
        value = value.replace(/\n/gi, '<br/>');
      }

      if (tokenId && !userId)
        tokens.push({
          token_id: Number(tokenId),
          value: value || null,
        });
      else {
        const userExists = users.find(user => user.id === Number(userId));

        if (userExists) {
          // if user already exists, just add the new token
          userExists.tokens.push({
            token_id: Number(tokenId),
            value: value || null,
          });
        } else {
          // else, create new user
          users.push({
            id: Number(userId),
            tokens: [
              {
                token_id: Number(tokenId),
                value: value || null,
              },
            ],
          });
        }
      }
    });

    return API.post(`document-wizard/step-three/${holderId}`, { tokens, users })
      .then(res => {
        const { next_step: nextStep } = res.data;
        // Set next current step into wizard context
        setCurrentStep(nextStep);
        // Push to next step
        const url = buildClientUrl(`/documents-wizard/step-${nextStep}/${holderId}`, client);

        queryClient.removeQueries(buildClientEntity('step-three', client));
        queryClient.removeQueries(buildClientEntity('step-four', client));
        queryClient.removeQueries(buildClientEntity('step-five', client));
        history.push(url);
      })
      .catch(res => {
        const response = createErrorFromResponse(res);
        const errors: any = {};
        Object.keys(submitData).forEach(key => {
          const parts = key.split('-');
          parts.shift();

          let userId: string | undefined;
          let tokenId: string | undefined;

          const lastPart = parts[parts.length - 1];
          const secondLastPart = parts[parts.length - 2];

          if (!Number.isNaN(Number(lastPart)) && !Number.isNaN(Number(secondLastPart))) {
            userId = parts.pop();
            tokenId = parts.pop();
          } else if (!Number.isNaN(Number(lastPart))) {
            tokenId = parts.pop();
          }

          const tokenError = response.errors[`field-${tokenId}`];
          if (tokenId && tokenError) errors[key] = tokenError;
        });
        setServerErrors(errors, setError);
      });
  };

  // Get selected value
  const getSelectedVal = (token: IToken, userId?: any) => {
    if (employeeTokens.length === 0 || !employeeTokens || !userId) {
      return getValues(`field-${token.type}-${token.token_id}` as never);
    }
    return getValues(`field-${token.type}-${token.token_id}-${userId}` as never);
  };

  // Find the index of selected option
  const getSelectedOptionIndex = (token: IToken, selectedVal: any) =>
    token.options?.findIndex((opt: any) => opt === selectedVal);

  const getEquivalentTokenDetails = (token: IToken, employee: IEmployeeToken | ''): IEquivalentTokenDetails | null => {
    if (!isMultilingual) {
      return null;
    }

    const employeeId = employee === '' ? undefined : employee.user.id;

    if (employeeId) {
      const employeeIndexBg = employeeTokensBg?.findIndex((item: IEmployeeToken) => item.user.id === employeeId) ?? -1;
      const employeeIndexEn = employeeTokensEn?.findIndex((item: IEmployeeToken) => item.user.id === employeeId) ?? -1;

      if (employeeIndexBg >= 0) {
        const tokenIndex = employeeTokensBg[employeeIndexBg].tokens.findIndex(
          (item: IToken) => item.token_id === token.token_id
        );

        if (tokenIndex >= 0 && employeeTokensEn?.[employeeIndexBg]?.tokens?.[tokenIndex]) {
          return {
            token: employeeTokensEn[employeeIndexBg].tokens[tokenIndex],
            userId: employeeId,
          };
        }
      }

      if (employeeIndexEn >= 0) {
        const tokenIndex = employeeTokensEn[employeeIndexEn].tokens.findIndex(
          (item: IToken) => item.token_id === token.token_id
        );

        if (tokenIndex >= 0 && employeeTokensBg?.[employeeIndexEn]?.tokens?.[tokenIndex]) {
          return {
            token: employeeTokensBg[employeeIndexEn].tokens[tokenIndex],
            userId: employeeId,
          };
        }
      }

      return null;
    }

    const globalIndexBg = globalTokensBg?.findIndex((item: IToken) => item.token_id === token.token_id) ?? -1;
    if (globalIndexBg >= 0 && globalTokensEn?.[globalIndexBg]) {
      return {
        token: globalTokensEn[globalIndexBg],
      };
    }

    const globalIndexEn = globalTokensEn?.findIndex((item: IToken) => item.token_id === token.token_id) ?? -1;
    if (globalIndexEn >= 0 && globalTokensBg?.[globalIndexEn]) {
      return {
        token: globalTokensBg[globalIndexEn],
      };
    }

    return null;
  };

  const syncEquivalentField = (token: IToken, employee: IEmployeeToken | '', value: any) => {
    const equivalent = getEquivalentTokenDetails(token, employee);

    if (!equivalent) {
      return;
    }

    const equivalentFieldName = equivalent.userId
      ? `field-${equivalent.token.type}-${equivalent.token.token_id}-${equivalent.userId}`
      : `field-${equivalent.token.type}-${equivalent.token.token_id}`;

    setValue(equivalentFieldName as never, value as never);
  };

  // Build field props based on field type
  const buildFieldProps = (token: IToken, employee: IEmployeeToken | '') => {
    let fieldProps: IFormControlProps;

    if (employee === '') {
      fieldProps = {
        name: `field-${token.type}-${token.token_id}`,
      };
    } else {
      fieldProps = {
        name: `field-${token.type}-${token.token_id}-${employee.user.id}`,
      };
    }

    const { type } = token;

    if (type === 'dropdown') {
      fieldProps.type = 'select';
      fieldProps.as = 'select';
      fieldProps.selectOptions = token.options;
      fieldProps.isClearable = !token.is_required;

      fieldProps.onChange = () => {
        if (!isMultilingual) {
          return;
        }

        const selectedVal = employee === '' ? getSelectedVal(token) : getSelectedVal(token, employee.user.id);
        const selectedOptionIndex = getSelectedOptionIndex(token, selectedVal);

        if (selectedOptionIndex === undefined || selectedOptionIndex < 0) {
          return;
        }

        const equivalent = getEquivalentTokenDetails(token, employee);
        const equivalentOption = equivalent?.token.options?.[selectedOptionIndex];

        if (!equivalent || !equivalentOption) {
          return;
        }

        syncEquivalentField(token, employee, equivalentOption);
      };
    }

    if (type === 'datepicker') {
      const slicedDateFormatTimeCheck = token.date_format!.split(' ');
      const hasTimePicker = !!slicedDateFormatTimeCheck[1];
      const hasYearPicker = token.date_format === 'yyyy';
      fieldProps.type = type;
      fieldProps.dateFormat = token.date_format;
      fieldProps.showTimeSelect = hasTimePicker;
      fieldProps.showYearPicker = hasYearPicker;

      fieldProps.onChange = () => {
        if (!isMultilingual) {
          return;
        }

        const selectedVal = employee === '' ? getSelectedVal(token) : getSelectedVal(token, employee.user.id);
        syncEquivalentField(token, employee, selectedVal);
      };
    }

    if (type === 'textarea') {
      fieldProps.type = 'text';
      fieldProps.as = 'textarea';
    }

    if (type === 'text') fieldProps.type = type;

    if (type === 'numeric-integer') {
      fieldProps.type = 'number';
      fieldProps.isNumber = true;

      fieldProps.onChange = () => {
        if (!isMultilingual) {
          return;
        }

        const selectedVal = employee === '' ? getSelectedVal(token) : getSelectedVal(token, employee.user.id);
        syncEquivalentField(token, employee, selectedVal);
      };
    }

    return fieldProps;
  };

  const isExternal = data.type === 'external';

  const buildBackStep = () => {
    // When total number of steps is 5, all steps are available, so the previous one is 2.
    // When total number of steps is 4, step 2 is skipped and we return to step 1.
    // When document is external, step 2 is always skipped.
    const internalDocCheck = totalSteps === 5 ? 2 : 1;
    return isExternal ? 1 : internalDocCheck;
  };

  return (
    <>
      <TokenLabelStyles />
      {employeeTokens && employeeTokens.length > 0 && (
        <ToastMessage
          userNameWithError={userNameWithError}
          show={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
      <Wizard {...buildWizardProps(isExternal, currentStep, 2, totalSteps)} />
      <Row className="mt-4">
        <Col md={{ offset: 2, span: 8 }}>
          <Form
            methods={methods}
            onSubmit={onSubmit}
            btnLabel={t('common.saveAndContinue')}
            isHorizontal
            hideCancelBtn
            areBtnsCentered
            showGlobalError
            isUploading={isFileUploading}
            onError={onError}
          >
            <WizardNavAndSubmit
              currentStep={currentStep}
              backStep={buildBackStep()}
              backStepInvisible={holderId}
              isSubmitting={isSubmitting || isFileUploading}
            />
            {/* In case of edit mode and empty tokens show missing tokens text */}
            {globalTokens && globalTokens.length === 0 && holderId && <p>{t('docWizard.step3.missingFieldsText')}</p>}
            {globalTokens && globalTokens.length > 0 && (
              <>
                <h4 className="text-center mb-4">
                  {t(holderId ? 'docWizard.step3.fieldsEditTitle' : 'docWizard.step3.fieldsTitle')}
                </h4>
                <Row className="mt-4">
                  <Col md={{ span: isMultilingual ? 6 : 12 }}>
                    {globalTokensBg.map((token: IToken) => (
                      <FormGroup
                        controlId={`formGroup${token.name}`}
                        label={token.name}
                        isHorizontal={!isMultilingual}
                        isRequired={token.is_required}
                        isDisabled={token.is_disabled}
                        hint={token.hint}
                        labelGrid={3}
                        controlGrid={isMultilingual ? 12 : 7}
                        formGroupClassName="u-label--wrap"
                        {...buildFieldProps(token, '')}
                      />
                    ))}
                  </Col>
                  {isMultilingual && (
                    <Col md={{ span: 6 }}>
                      {globalTokensEn.map((token: IToken) => (
                        <FormGroup
                          controlId={`formGroup${token.name}`}
                          label={token.name}
                          isHorizontal={!isMultilingual}
                          isRequired={token.is_required}
                          isDisabled={token.is_disabled}
                          hint={token.hint}
                          hintClass="u-visibility--hidden"
                          labelGrid={3}
                          controlGrid={12}
                          {...buildFieldProps(token, '')}
                        />
                      ))}
                    </Col>
                  )}
                </Row>
              </>
            )}

            {employeeTokens && employeeTokens.length > 0 && (
              <>
                <InfoContainer>
                  <FaInfoCircle className="mr-2" />
                  <span>
                    <i>
                      {textParts[0]} <br /> {textParts[1]}
                    </i>
                  </span>
                </InfoContainer>
                <hr />
                {employeeTokensBg.map((employee: any, index: number) => (
                  <Card key={employee.user.id}>
                    <Card.Header
                      onClick={() =>
                        setOpenCards(prevState => ({ ...prevState, [employee.user.id]: !prevState[employee.user.id] }))
                      }
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <h5
                        style={
                          errorNamesRef.current.some(errorName => errorName.endsWith(`-${employee.user.id}`))
                            ? { color: '#ef476f' }
                            : {}
                        }
                      >
                        {employee.user.name}
                      </h5>
                      {openCards[employee.user.id] ? <BiChevronLeft size={20} /> : <BiChevronDown size={20} />}
                    </Card.Header>
                    <Accordion.Collapse
                      eventKey={index.toString()}
                      in={openCards[employee.user.id]}
                    >
                      <Card.Body>
                        <Row className="mt-4">
                          <Col>
                            <div>
                              {employee.tokens.map((token: IToken, tokenIndex: number) => {
                                if (tokenIndex % 2 !== 0 && !isMultilingual) return null;

                                return (
                                  <Row key={token.name + employee.user.id}>
                                    <Col md={6}>
                                      <FormGroup
                                        controlId={`formGroupBg${token.name}`}
                                        label={token.name}
                                        isRequired={token.is_required}
                                        isDisabled={token.is_disabled}
                                        hint={token.hint}
                                        {...buildFieldProps(token, employee)}
                                      />
                                    </Col>

                                    {isMultilingual && employeeTokensEn[index] ? (
                                      <Col md={6}>
                                        <FormGroup
                                          controlId={`formGroupEn${employeeTokensEn[index].tokens[tokenIndex].name}`}
                                          label={employeeTokensEn[index].tokens[tokenIndex].name}
                                          isRequired={employeeTokensEn[index].tokens[tokenIndex].is_required}
                                          isDisabled={employeeTokensEn[index].tokens[tokenIndex].is_disabled}
                                          hint={employeeTokensEn[index].tokens[tokenIndex].hint}
                                          hintClass="u-visibility--hidden"
                                          {...buildFieldProps(employeeTokensEn[index].tokens[tokenIndex], employee)}
                                        />
                                      </Col>
                                    ) : (
                                      tokenIndex < employee.tokens.length - 1 && (
                                        <Col md={6}>
                                          <FormGroup
                                            controlId={`formGroupBg${employee.tokens[tokenIndex + 1].name}`}
                                            label={employee.tokens[tokenIndex + 1].name}
                                            isRequired={employee.tokens[tokenIndex + 1].is_required}
                                            isDisabled={employee.tokens[tokenIndex + 1].is_disabled}
                                            hint={employee.tokens[tokenIndex + 1].hint}
                                            {...buildFieldProps(employee.tokens[tokenIndex + 1], employee)}
                                          />
                                        </Col>
                                      )
                                    )}
                                  </Row>
                                );
                              })}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                ))}
              </>
            )}

            {!holderId && (
              <>
                <hr />
                <h4 className="text-center mb-4">{t('docWizard.step3.attachDocuments')}</h4>
                <FormGroup
                  controlId="formGroupFileUpload"
                  type="file"
                  placeholder={t('fileUploader.placeholder')}
                  name="file"
                  isHorizontal
                  accept="application/pdf"
                  multiple
                  isRequired={files.length === 0 ? isExternal : false}
                  uploadUri="document-wizard/upload-file"
                  deleteFileUri="document-wizard/external-documents"
                  hint={t('fileUploader.hint', { accept: 'pdf' })}
                  controlGrid={12}
                  isFileUploading={(isUploading: boolean) => {
                    setIsFileUploading(isUploading);
                  }}
                />
                {files.length > 0 && <Files files={files} />}
              </>
            )}
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default StepThree;

const TokenLabelStyles = createGlobalStyle`
  .u-label--wrap label {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  @media ${props => props.theme.breakpoints.sm} {
    flex-direction: column;
    gap: 5px;

    .mr-2 {
      margin-right: 0;
    }
  }
`;
